import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { trackAddToCart } from '../utils/analytics';
import { hapticSuccess } from '../utils/haptic';

// Create the cart context
const CartContext = createContext(null);

// Custom hook to use the cart context
export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

// Cart Provider component
export const CartProvider = ({ children }) => {
  // Load cart from localStorage on initial render
  const [cart, setCart] = useState(() => {
    try {
      const savedCart = localStorage.getItem('twoGsCraftingCart');
      return savedCart ? JSON.parse(savedCart) : [];
    } catch (error) {
      console.error('Error loading cart from localStorage:', error);
      return [];
    }
  });

  const [generalNote, setGeneralNote] = useState(() => {
    try {
      const savedNote = localStorage.getItem('twoGsCraftingNote');
      return savedNote || '';
    } catch (error) {
      return '';
    }
  });

  const [isCartOpen, setIsCartOpen] = useState(false);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem('twoGsCraftingCart', JSON.stringify(cart));
    } catch (error) {
      console.error('Error saving cart to localStorage:', error);
    }
  }, [cart]);

  // Save general note to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem('twoGsCraftingNote', generalNote);
    } catch (error) {
      console.error('Error saving note to localStorage:', error);
    }
  }, [generalNote]);

  // Add to cart as a new cart item each time, to allow separate personalizations
  const addToCart = useCallback((item, openCart = true) => {
    // Generate a unique ID for this cart item instance
    const uniqueId = `${item.id}-${Date.now()}`;

    // Calculate the actual price (with any discount applied)
    const actualPrice = item.discount > 0
      ? item.price * (1 - item.discount / 100)
      : item.price;

    // Add new item to cart with its own personalization field and customization images array
    setCart(prevCart => [...prevCart, {
      ...item,
      cartItemId: uniqueId,
      productId: item.id,
      quantity: 1,
      personalization: '',
      customizationImages: [], // Initialize empty array for images
      // Use the actual price (with discount if applicable)
      price: parseFloat(actualPrice.toFixed(2))
    }]);

    // Track add to cart event in analytics
    trackAddToCart(item, 1);

    // Haptic feedback for successful add to cart
    hapticSuccess();

    // Automatically open the cart modal when adding an item (if specified)
    if (openCart) {
      setIsCartOpen(true);
    }

    return uniqueId;
  }, []);

  const addConfiguredItem = useCallback((item, configuration, openCart = false) => {
    const uniqueId = `${item.id}-${Date.now()}`;
    const numericPrice = Number(item.price);
    const hasConfirmedPrice = item.price !== null && Number.isFinite(numericPrice);
    const quantity = Math.max(1, Number.parseInt(configuration.quantity, 10) || 1);
    const structuredPersonalisation = {
      name: configuration.name.trim(),
      message: configuration.message.trim(),
      messageMode: configuration.messageMode,
      previewTemplate: item.personalisation?.previewTemplate || 'default',
      collectionId: configuration.collectionId,
      collectionSlug: configuration.collectionSlug,
      collectionName: configuration.collectionName
    };
    setCart((current) => [...current, {
      ...item,
      cartItemId: uniqueId,
      productId: item.id,
      quantity,
      price: hasConfirmedPrice ? numericPrice : 0,
      pricePending: !hasConfirmedPrice,
      personalization: `${structuredPersonalisation.name} — ${structuredPersonalisation.message}`,
      personalisationDetails: structuredPersonalisation,
      customizationImages: []
    }]);
    trackAddToCart({ ...item, price: hasConfirmedPrice ? numericPrice : 0 }, quantity);
    hapticSuccess();
    if (openCart) setIsCartOpen(true);
    return uniqueId;
  }, []);

  const updateConfiguredItem = useCallback((cartItemId, configuration) => {
    setCart((current) => current.map((cartItem) => {
      if (cartItem.cartItemId !== cartItemId) return cartItem;
      const quantity = Math.max(1, Number.parseInt(configuration.quantity, 10) || 1);
      const personalisationDetails = {
        ...cartItem.personalisationDetails,
        name: configuration.name.trim(),
        message: configuration.message.trim(),
        messageMode: configuration.messageMode
      };
      return {
        ...cartItem, quantity, personalisationDetails,
        personalization: `${personalisationDetails.name} — ${personalisationDetails.message}`
      };
    }));
    hapticSuccess();
  }, []);

  // Update item quantity in cart
  const updateQuantity = useCallback((cartItemId, newQuantity) => {
    // If quantity is zero or negative, remove the item from cart
    if (newQuantity <= 0) {
      setCart(prevCart => prevCart.filter(item => item.cartItemId !== cartItemId));
      return;
    }

    // Otherwise update the quantity
    setCart(prevCart =>
      prevCart.map(item =>
        item.cartItemId === cartItemId ? { ...item, quantity: newQuantity } : item
      )
    );
  }, []);

  // Update personalization for a specific cart item
  const updatePersonalization = useCallback((cartItemId, text) => {
    setCart(prevCart =>
      prevCart.map(item =>
        item.cartItemId === cartItemId ? { ...item, personalization: text } : item
      )
    );
  }, []);

  // Update customization images for a specific cart item
  const updateCustomizationImages = useCallback((cartItemId, newImages, replace = false) => {
    setCart(prevCart =>
      prevCart.map(item => {
        if (item.cartItemId === cartItemId) {
          return {
            ...item,
            customizationImages: replace
              ? newImages
              : [...(item.customizationImages || []), ...newImages]
          };
        }
        return item;
      })
    );
  }, []);

  // Remove item from cart
  const removeFromCart = useCallback((cartItemId) => {
    setCart(prevCart => prevCart.filter(item => item.cartItemId !== cartItemId));
  }, []);

  // Clear the entire cart
  const clearCart = useCallback(() => {
    setCart([]);
    setGeneralNote('');
    try {
      localStorage.removeItem('twoGsCraftingCart');
      localStorage.removeItem('twoGsCraftingNote');
    } catch (error) {
      console.error('Error clearing cart from localStorage:', error);
    }
  }, []);

  const removeCollectionItems = useCallback((collectionSlug) => {
    setCart((current) => current.filter((item) => item.personalisationDetails?.collectionSlug !== collectionSlug));
  }, []);

  // Group cart items by product ID to display in a structured way
  const groupedCartItems = cart.reduce((acc, item) => {
    if (!acc[item.productId]) {
      acc[item.productId] = {
        name: item.name,
        price: item.price,
        image: item.image,
        media: item.media, // Include media array for new products
        pricePending: item.pricePending || false,
        collectionSlug: item.personalisationDetails?.collectionSlug || null,
        items: []
      };
    }

    acc[item.productId].items.push({
      cartItemId: item.cartItemId,
      quantity: item.quantity,
      personalization: item.personalization,
      personalisationDetails: item.personalisationDetails,
      pricePending: item.pricePending || false,
      customizationImages: item.customizationImages || []
    });

    return acc;
  }, {});

  // Calculate total items in cart
  const totalItems = cart.reduce((sum, item) => sum + item.quantity, 0);

  // Calculate total cost
  const totalCost = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);

  // Open cart modal
  const openCartModal = useCallback(() => {
    setIsCartOpen(true);
  }, []);

  // Close cart modal
  const closeCartModal = useCallback(() => {
    setIsCartOpen(false);
  }, []);

  const value = {
    // State
    cart,
    generalNote,
    isCartOpen,
    groupedCartItems,
    totalItems,
    totalCost,

    // Setters
    setGeneralNote,

    // Cart actions
    addToCart,
    addConfiguredItem,
    updateConfiguredItem,
    updateQuantity,
    updatePersonalization,
    updateCustomizationImages,
    removeFromCart,
    clearCart,
    removeCollectionItems,

    // Modal actions
    openCartModal,
    closeCartModal,
    setIsCartOpen,
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

export default CartContext;
